import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { messagesAtom } from "~/Atom";
import {
  locationList,
  locationListType,
  locationInfoAtom,
  getCurrentCustomerId,
  styleModeAtom,
} from "~/AtomWithDynamoDB";
import { api } from "~/api";

type LatLon = {
  lat: number;
  lon: number;
};

// バリデーションテキストの色状態を管理するための定数
export const VALIDATE_STATES = {
  INFO: "info",
  ERROR: "error",
} as const;

type ValidateState = (typeof VALIDATE_STATES)[keyof typeof VALIDATE_STATES];

type ValidationInfo = {
  message: string;
  state: ValidateState;
};

/**
 * 場所情報の設定に関するhooks
 */
export const useLocationSettings = () => {
  const [locationInfo, setLocationInfo] = useAtom(locationInfoAtom);
  const [, setMessages] = useAtom(messagesAtom);
  const [styleMode] = useAtom(styleModeAtom);
  const { t } = useTranslation();

  const [locationInputJA, setLocationInputJA] = useState<string>("");
  const [locationInputEN, setLocationInputEN] = useState<string>("");
  const [locationInputLatLon, setLocationInputLatLon] = useState<LatLon | null>(null);
  const [locationInputSearchAddress, setLocationInputSearchAddress] = useState<string>("");
  const [locationInputAddress, setLocationInputAddress] = useState<string>("");

  const [isSearching, setIsSearching] = useState(false);

  const [searchValidation, setSearchValidation] = useState<ValidationInfo>({
    message: "",
    state: VALIDATE_STATES.INFO,
  });
  const [saveValidation, setSaveValidation] = useState<ValidationInfo>({
    message: "",
    state: VALIDATE_STATES.INFO,
  });

  /**
   * locationInfoの変更を検知し、画面の入力欄に反映する
   */
  useEffect(() => {
    setLocationInputJA(locationInfo.jaName);
    setLocationInputEN(locationInfo.enName);
    setLocationInputLatLon({ lat: locationInfo.lat, lon: locationInfo.lon });
    setLocationInputAddress(locationInfo.address);
  }, [locationInfo]);

  // 未保存の変更を検知
  useEffect(() => {
    // 必須項目が未入力の場合、未保存の変更は検知しない
    if (!locationInputJA || !locationInputEN || !locationInputLatLon || !locationInputAddress) {
      setSaveValidation({ message: "", state: VALIDATE_STATES.INFO });
      return;
    }

    // 初期値と異なる場合、未保存の変更があるとみなす
    const hasUnsavedChanges = [
      locationInputJA !== locationInfo.jaName,
      locationInputEN !== locationInfo.enName,
      locationInputAddress !== locationInfo.address,
      locationInputLatLon?.lat !== locationInfo.lat,
      locationInputLatLon?.lon !== locationInfo.lon,
    ].some(Boolean);

    setSaveValidation({
      message: hasUnsavedChanges ? t("notSaved") : "",
      state: VALIDATE_STATES.INFO,
    });
  }, [locationInputJA, locationInputEN, locationInputAddress, locationInputLatLon, locationInfo]);

  const handleLocationChangeJA = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLocationInputJA(e.target.value);
  const handleLocationChangeEN = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLocationInputEN(e.target.value);
  const handleLocationChangeSearchAddress = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLocationInputSearchAddress(e.target.value);

  /**
   * 場所検索ボタンのクリックハンドラー
   * 非同期処理を行い、検索中はボタンを無効化する
   * 検索が完了したら再度ボタンを有効化する
   * エラーハンドリングはsearchLocation内で行う
   */
  const handleSearchClick = async () => {
    // 検索アニメーション開始
    setIsSearching(true);
    try {
      await searchLocation();
    } finally {
      // 検索アニメーション終了
      setIsSearching(false);
    }
  };

  /**
   * 検索中はボタンを無効化するためのフラグ
   */
  const isSearchingDisabled = isSearching || locationInputSearchAddress.trim() === "";

  /**
   * 住所または郵便番号から緯度経度を取得する処理
   */
  const searchLocation = async () => {
    try {
      // 空白を削除
      const trimmedQuery = locationInputSearchAddress.trim();
      // 全角数字を半角数字に変換
      const normalizedQuery = trimmedQuery.replace(/[０-９]/g, (match) => {
        return String.fromCharCode(match.charCodeAt(0) - 0xfee0);
      });

      const latLonResponse = await api.dify.getLatLon.query({ locationQuery: normalizedQuery });
      setLocationInputLatLon({ lat: latLonResponse.lat, lon: latLonResponse.lon }); // 緯度経度を内部状態に保存
      setLocationInputAddress(latLonResponse.address); // 住所を表示

      console.info(`取得した緯度経度: ${latLonResponse.lat}, ${latLonResponse.lon}`); // 緯度経度をコンソールに表示

      setSearchValidation({ message: "", state: VALIDATE_STATES.INFO }); // バリデーションをクリア
    } catch (error) {
      // 住所または郵便番号が正しくない場合のエラーメッセージを表示
      setSearchValidation({ message: t("locationSearchValidation"), state: VALIDATE_STATES.ERROR });
      console.error("住所の取得に失敗しました:", error);
    }
  };

  /**
   * バリデーションをクリアする処理
   */
  const clearValidation = () => {
    setSearchValidation({ message: "", state: VALIDATE_STATES.INFO });
    setSaveValidation({ message: "", state: VALIDATE_STATES.INFO });
  };

  /**
   * 場所情報を保存する処理
   */
  const saveLocation = (onSuccess: () => void) => {
    const ja = locationInputJA.trim();
    const en = locationInputEN.trim();
    const lat = locationInputLatLon?.lat;
    const lon = locationInputLatLon?.lon;
    const address = locationInputAddress.trim();

    if (!ja || !en || !address || !lat || !lon) {
      // 空の項目がある場合、保存しない
      setSaveValidation({ message: t("locationFieldsValidation"), state: VALIDATE_STATES.ERROR });
      return;
    }
    const next: locationListType = { ...locationInfo, jaName: ja, enName: en, lat, lon, address };
    setLocationInfo(next);

    // デフォルトメッセージを更新
    const lines = t(`defaultMessage.${styleMode}`, {
      returnObjects: true,
      LOCATION_JA_NAME: next.jaName,
      LOCATION_EN_NAME: next.enName,
    }) as string[];
    setMessages(
      lines.map((content) => ({
        role: "assistant",
        content,
        messageId: "0",
      }))
    );

    // バリデーションをクリア
    clearValidation();

    onSuccess();
  };

  /**
   * AWS環境でDynamoDBから場所情報を取得する処理
   * @returns DynamoDBから取得した場所情報
   */
  const fetchLocationInfoFromDynamoDB = async (): Promise<typeof locationInfo> => {
    try {
      // DynamoDBから場所情報を取得
      const customerId = await getCurrentCustomerId();
      const CUSTOMER_TABLE_NAME = process.env.REACT_APP_AWS_DYNAMODB_CUSTOMER_TABLE_NAME as string;

      const response = await api.dynamodb.getItem.query({
        tableName: CUSTOMER_TABLE_NAME,
        key: { customerId: customerId },
      });

      // 取得成功時は場所情報を返す
      if (response.success && "item" in response && response.item) {
        const dbLocationInfo = response.item as typeof locationInfo;
        return {
          jaName: dbLocationInfo.jaName ?? "",
          enName: dbLocationInfo.enName ?? "",
          lat: (dbLocationInfo.lat as number | undefined) ?? locationList.lat,
          lon: (dbLocationInfo.lon as number | undefined) ?? locationList.lon,
          address: dbLocationInfo.address ?? "",
        };
      }
      // 取得失敗時はデフォルト値を返す
      return {
        jaName: locationList.jaName,
        enName: locationList.enName,
        lat: locationList.lat,
        lon: locationList.lon,
        address: locationList.address,
      };
    } catch (error) {
      console.error("DynamoDBから場所情報の取得に失敗しました:", error);
      return {
        jaName: locationList.jaName,
        enName: locationList.enName,
        lat: locationList.lat,
        lon: locationList.lon,
        address: locationList.address,
      };
    }
  };

  /**
   * 場所情報をリセットする処理
   */
  const resetLocation = async () => {
    // AWS環境かどうかを判定
    const isAWS = process.env.REACT_APP_SYSTEM_REQUIREMENT === "aws";

    if (isAWS) {
      // AWS環境: DynamoDBから最新の値を取得してリセット
      const dynamoDBLocationInfo = await fetchLocationInfoFromDynamoDB();

      setLocationInputJA(dynamoDBLocationInfo.jaName);
      setLocationInputEN(dynamoDBLocationInfo.enName);
      setLocationInputLatLon({ lat: dynamoDBLocationInfo.lat, lon: dynamoDBLocationInfo.lon });
      setLocationInputAddress(dynamoDBLocationInfo.address);
    } else {
      // オンプレ環境: デフォルト値(初期値)にリセット
      setLocationInputJA(locationList.jaName);
      setLocationInputEN(locationList.enName);
      setLocationInputLatLon({ lat: locationList.lat, lon: locationList.lon });
      setLocationInputAddress(locationList.address);
    }

    // 住所検索のクリア
    setLocationInputSearchAddress("");

    // バリデーションをクリア
    clearValidation();
  };

  return {
    locationInputJA,
    locationInputEN,
    locationInputSearchAddress,
    locationInputAddress,
    searchValidation,
    saveValidation,
    isSearching,
    isSearchingDisabled,
    handleLocationChangeJA,
    handleLocationChangeEN,
    handleLocationChangeSearchAddress,
    handleSearchClick,
    searchLocation,
    saveLocation,
    resetLocation,
  };
};
